/**
 * Centralized image URL utilities for coffee shop platform
 * Prevents hardcoded URLs and ensures consistent image handling
 */

import { API_BASE_URL } from '../config/api';

// Detect if a string is already an absolute URL
const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url || '');

// Normalize filename or path to just the basename when needed
const toFilename = (value) => {
  if (!value) return '';
  try {
    // If it's an absolute URL and already points to our assets path, return as-is
    if (isAbsoluteUrl(value)) return value;
    // Strip any leading slashes or nested paths to avoid duplications
    return value.split('/').pop();
  } catch {
    return value;
  }
};

/**
 * Get the full URL for a product image
 * Accepts filename (e.g., 'americano.jpg') or absolute URL; returns normalized URL
 */
export const getProductImageUrl = (image) => {
  if (!image) return '';
  if (isAbsoluteUrl(image)) return image;
  const filename = toFilename(image);
  return `${API_BASE_URL}/assets/products/${filename}`;
};

/**
 * Get the full URL for a shop logo
 * @param {string} logoFilename - The logo filename
 * @returns {string} - Full logo URL
 */
export const getLogoUrl = (logoFilename) => {
  if (!logoFilename) return '';
  if (isAbsoluteUrl(logoFilename)) return logoFilename;
  const filename = toFilename(logoFilename);
  return `${API_BASE_URL}/assets/logos/${filename}`;
};

/**
 * Get the full URL for any asset
 * @param {string} assetPath - The asset path (e.g., 'products/americano.jpg')
 * @returns {string} - Full asset URL
 */
export const getAssetUrl = (assetPath) => {
  if (!assetPath) return '';
  if (isAbsoluteUrl(assetPath)) return assetPath;
  const path = assetPath.replace(/^\/+/, '');
  return `${API_BASE_URL}/assets/${path}`;
};

/**
 * Check if an image URL is valid
 * @param {string} url - The image URL to validate
 * @returns {boolean} - True if valid
 */
export const isValidImageUrl = (url) => {
  return url && typeof url === 'string' && url.trim().length > 0;
};

/**
 * Get a fallback image URL if the main image fails
 * @param {string} fallbackType - Type of fallback ('product', 'logo', 'default')
 * @returns {string} - Fallback image URL
 */
export const getFallbackImageUrl = (fallbackType = 'default') => {
  const fallbackImages = {
    product: `${API_BASE_URL}/assets/products/default-product.jpg`,
    logo: `${API_BASE_URL}/assets/logos/default-logo.png`,
    default: `${API_BASE_URL}/assets/default-image.jpg`
  };
  
  return fallbackImages[fallbackType] || fallbackImages.default;
};
